import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import memoize from 'lodash/memoize';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MUILink,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, purple, teal } from '@mui/material/colors';

import { Order } from '@luxuryescapes/contract-svc-order';
import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import EmployeeUserList from '~/components/Common/EmployeeUserList';
import UpdateUser from '~/components/Common/UpdateUser';
import {
  checkCanRedeemLuxPlusBenefits,
  checkCanViewLuxPlusBenefits,
  hasLuxPlusSubscriptionStatus,
  isFreePreviewLuxPlusMember,
} from '~/components/Membership/utils';
import UserAbandonedCartsTable from '~/components/System/UserAbandonedCartsTable';
import CreatePromoForUserModal from '~/components/Users/CreatePromoForUserModal';
import UpgradeToLuxPlusButton from '~/components/Users/UserDetails/UpgradeToLuxPlusButton';

import { CUDO, DEALS, LE_AGENT_HUB, LE_AGENT_HUB_CVFR, LUXURY_ESCAPES, SCOOPON, TREAT_ME } from '~/consts/brands';
import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { hasBusinessAdminOrManagerRole } from '~/consts/businessTravellerRoles';
import { LUX_PLUS } from '~/consts/membership';
import {
  ROLE_ADMIN_USER,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';
import TENANTS from '~/consts/tenant';

import { useGetEmployee } from '~/hooks/apis/business-traveller/useGetEmployee';

import { formatDateOrdinalWithClock } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

import { apiToFormBusinessDetails } from '~/utils/businessDetailsMapping';
import canShowTabExceptRoles from '~/utils/canShowTabExceptRoles';
import { isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';
import { isAgentHubSubTenant } from '~/utils/tenantUtils';

import PermissionedComponent from '../Common/PermissionedComponent';
import UserAgencyDetails from '../LeAgentHub/components/UserAgencyDetails';
import LuxLoyaltyAccountChip from '../LuxLoyalty/LuxLoyaltyAccountChip';
import LuxLoyaltyUserProfile from '../LuxLoyalty/LuxLoyaltyUserProfile';
import MembershipSubscriptionDetails from '../Membership/MembershipSubscriptionDetails';
import UserEmailChangeTable from '../System/UserEmailChangeTable';
import UserLoginTable from '../System/UserLoginTable';
import UserRecentOfferPageViewsTable from '../System/UserRecentOfferPageViewsTable';
import UserRecentSearchTable from '../System/UserRecentSearchTable';
import FraudWarning from '../Users/History/FraudWarning';
import SamePhoneAccounts from '../Users/History/SamePhoneAccounts';
import { withTenant } from '../hoc';

import AtolCertificatesTab from './AtolCertificates/AtolCertificatesTab';
import CustomOffersTab from './CustomOffers/CustomOffersTab';
import ForceLogoutModal from './ForceLogoutModal';
import FrequentFlyerMembershipsTab from './FrequentFlyerMemberships/FrequentFlyerMembershipsTab';
import GDPRRemovalModal from './GRPRRemovalModal';
import GDPRRequestModal from './GRPRRequestModal';
import QuotesTab from './Quotes/QuotesTab';
import SimilarAccountsTab from './SimilarAccounts/SimilarAccountsTab';
import BusinessAdminSpoofButton from './UserDetails/BusinessAdminSpoofButton';
import BusinessTravellerBusinessLabel from './UserDetails/BusinessTravellerBusinessLabel';
import EmailStatusCard from './UserDetails/DeliveryStatusCard';
import PartnershipButtons from './UserDetails/PartnershipButtons';
import ResolveAccountTakeoverInstructions from './UserDetails/ResolveAccountTakeoverInstructions';
import SpoofUserBlock from './UserDetails/SpoofUserBlock';
import TenancyWarningMessage from './UserDetails/TenancyWarningMessage';
import VerifyCustomerButtons from './UserDetails/VerifyCustomer/VerifyCustomerButtons';
import { validUserTenantForSiteTenant } from './UserDetails/utils';

const getRegionsByCountryCode = memoize((regions) => {
  return regions.reduce((regionsByCountryCode, region) => {
    regionsByCountryCode[region.code] = region;
    return regionsByCountryCode;
  }, {});
});

type TabKey =
  | 'user-details'
  | 'quotes'
  | 'loyalty-programs-memberships'
  | 'custom-offers'
  | 'luxplus-member-profile'
  | 'user-agency'
  | 'recent-searches'
  | 'similar-accounts'
  | 'recent-offer-page-views'
  | 'luxloyalty-member-profile'
  | 'abandoned-carts'
  | 'atol-certificates';

export type UserTenancy = { id: string; tenant: string }; // id is the id of the user

type Props = {
  user: App.User;
  isAdmin: boolean;
  tenant: App.Tenant;
  tenancy: Array<UserTenancy>;
  schema: { put: RJSFSchema };
  regions: Array<App.Region>;
  updatePreferredAgent: (agent: string) => void;
  update: (user: App.User) => void;
  subscriptions: App.MembershipSubscriptions | null;
  luxLoyaltyAccount: App.LuxLoyaltyAccount | null;
  currentSubscriptionOrderItem: Order.SubscriptionItem;
  currentSubscriptionOrder: App.Order;
  refreshData: () => Promise<void>;
  context?: { user: any };
};

const newCustomOfferUri = (user: App.User, offerType = 'cruise') => {
  return `/users/${user.id_member}/custom-offers/create?${offerType}=true`;
};

const historyUri = (user: App.User) => {
  return `/users/${user.id_member}/history`;
};

function UserDetails(props: Props) {
  const history = useHistory();
  const location = useLocation();
  const UpdateUserFormRef = useRef<any>(null);
  const queryClient = useQueryClient();

  const {
    user,
    isAdmin,
    tenant,
    tenancy,
    schema,
    regions,
    updatePreferredAgent,
    update,
    subscriptions,
    luxLoyaltyAccount,
    currentSubscriptionOrderItem,
    currentSubscriptionOrder,
    refreshData,
  } = props;

  const { data: businessDetailsResult } = useQuery({
    queryKey: ['users', user.id_member, 'businessDetails'],
    queryFn: ({ signal }) => UsersService.getUserBusinessDetails(user.id_member, { signal }),
  });

  const { data: employee } = useGetEmployee(user.id_member, tenant.brand === LE_BUSINESS_TRAVELLER);

  const initialBusinessDetails = apiToFormBusinessDetails(businessDetailsResult);
  const handleBusinessDetailsSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['users', user.id_member, 'businessDetails'] });
  }, [queryClient, user.id_member]);

  const urlSearchParams = new URLSearchParams(location.search);
  const showLuxPlusTabInitially =
    urlSearchParams.get('view_luxplus_membership') === 'true' && checkCanViewLuxPlusBenefits(subscriptions);

  const [offer_id, setOfferId] = useState('');
  const [isCreatePromoCodeModalVisible, setIsCreatePromoCodeModalVisible] = useState(false);
  const [isGDPRRemovalModalVisible, setIsGDPRRemovalModalVisible] = useState(false);
  const [isGDPRRequestModalVisible, setIsGDPRRequestModalVisible] = useState(false);
  const [isForceLogoutModalVisible, setIsForceLogoutModalVisible] = useState(false);
  const [isEmailStatusDialogOpen, setIsEmailStatusDialogOpen] = useState(false);
  const [showFlightsWarningModal, setShowFlightsWarningModal] = useState(false);

  const [currentTab, setCurrentTab] = useState<TabKey>(
    showLuxPlusTabInitially ? 'luxplus-member-profile' : 'user-details',
  );

  const [hasBusinessAdminSpoofRoles, setHasBusinessAdminSpoofRoles] = useState<boolean>(false);

  const setCurrentTabHandler = (tab: TabKey) => {
    if (currentTab === 'luxplus-member-profile') {
      clearSearchParams();
    }
    setCurrentTab(tab);
  };

  const buyPageUri = (offer_id: string) => {
    return '/offers/' + offer_id + '/buy_for/' + user.id_member;
  };

  const purchasePageUri = () => {
    return `/purchases/section/allOrders?customer_id=${user.id_member}`;
  };

  const creditsPageUri = (userCountry) => {
    if (!userCountry) {
      return '#';
    }

    return `/users/${user.id_member}/credits?currency=${userCountry.currencyCode}`;
  };

  const promoRequestPageUri = () => {
    return `/marketing/promo-requests?userId=${user.id_member}&page=0&limit=100`;
  };

  const referralLogPageUri = () => {
    return `/marketing/referralLogs?q=${user.id_member}`;
  };

  const newOrderPageUri = () => {
    return `/users/${user.id_member}/new-order/offer-selection`;
  };

  const newFlightPageUri = () => {
    return `/users/${user.id_member}/new-flight-order/traveller`;
  };

  const newGiftCardOrderPageUri = () => {
    return `/users/${user.id_member}/new-gift-card-order/gift-card-selection`;
  };

  const newInsuranceOrderPageUri = () => {
    return `/users/${user.id_member}/new-insurance-order/insurance`;
  };

  const newAtolPageUri = () => {
    return `/users/${user.id_member}/atol`;
  };

  const newSentEmailUri = () => {
    return `/email/sent?email=${encodeURIComponent(user.email)}`;
  };

  const handleBuyOfferChange = (event) => {
    setOfferId(event.target.value);
  };

  const handleBuyOfferSubmit = (event) => {
    event.preventDefault();
    history.push(buyPageUri(offer_id));
  };

  const updateUser = () => {
    UpdateUserFormRef.current?.submitButton.click();
  };

  const gdprRemoval = () => {
    setIsGDPRRemovalModalVisible(true);
  };

  const gdprRequest = () => {
    setIsGDPRRequestModalVisible(true);
  };

  const hideGDPRRemovalModal = () => {
    setIsGDPRRemovalModalVisible(false);
  };

  const hideGDPRRequestModal = () => {
    setIsGDPRRequestModalVisible(false);
  };

  const showCreatePromoCodeModal = () => {
    setIsCreatePromoCodeModalVisible(true);
  };

  const showForceLogoutModal = () => {
    setIsForceLogoutModalVisible(true);
  };

  const hideCreatePromoCodeModal = () => {
    setIsCreatePromoCodeModalVisible(false);
  };

  const hideForceLogoutModal = () => {
    setIsForceLogoutModalVisible(false);
  };

  const showEmailStatusDialog = () => {
    setIsEmailStatusDialogOpen(true);
  };

  const hideEmailStatusDialog = () => {
    setIsEmailStatusDialogOpen(false);
  };

  const showSpoofUserBlock = (tenant, user, userTenancies) => {
    if (tenant.value === TENANTS.scoopon.value) {
      const scooponUserId = userTenancies.find((el) => el.tenant === TENANTS.scoopon.value)?.id;
      return user.id_member === scooponUserId;
    }

    return validUserTenantForSiteTenant(tenant.value, user.tenant);
  };

  const getFullTitle = () => {
    const shadowBanned = user.shadow_ban_user ? ' Shadow banned' : '';
    const vipClient = user.vip_client ? ' (VIP Client)' : '';
    const hasDash = shadowBanned || vipClient ? ' -' : '';

    return user.fullName + hasDash + shadowBanned + vipClient;
  };

  const canRedeemLuxPlusBenefits =
    tenant.brand !== LE_BUSINESS_TRAVELLER && checkCanRedeemLuxPlusBenefits(subscriptions);
  const luxPlusFreePreview = tenant.brand !== LE_BUSINESS_TRAVELLER && isFreePreviewLuxPlusMember(subscriptions);
  const canViewLuxPlusBenefits = tenant.brand !== LE_BUSINESS_TRAVELLER && checkCanViewLuxPlusBenefits(subscriptions);
  const hasLuxLoyaltyAccount = !!luxLoyaltyAccount;

  const getSubtitle = () => {
    const luxPlusFreePreviewText = luxPlusFreePreview ? 'Free Preview ' : '';
    const luxPlusMemberText = canViewLuxPlusBenefits
      ? ` ${luxPlusFreePreviewText} ${LUX_PLUS.PROGRAM_NAME} Member`
      : '';

    const content = [];

    if (luxPlusMemberText) {
      content.push(
        <Typography component="span" color={getLuxPlusColour()} fontSize="inherit" fontWeight={500}>
          {luxPlusMemberText}
        </Typography>,
      );
    }

    if (isLuxLoyaltyEnabled() && hasLuxLoyaltyAccount) {
      content.push(<LuxLoyaltyAccountChip luxLoyaltyAccount={luxLoyaltyAccount} size="medium" />);
    }

    if (tenant.brand === 'lebusinesstraveller') {
      content.push(<BusinessTravellerBusinessLabel customer={user} />);
    }

    return (
      content.length > 0 && (
        <Stack direction="row" gap={2} alignItems="center">
          {content.map((item, idx) => (
            <Fragment key={idx}>
              {item}
              {content.length > 1 && idx !== content.length - 1 && (
                <Divider orientation="vertical" flexItem variant="middle" sx={{ borderColor: '#000' }} />
              )}
            </Fragment>
          ))}
        </Stack>
      )
    );
  };

  const clearSearchParams = () => {
    history.replace({
      search: '',
    });
  };

  useEffect(() => {
    // Add the searched user ID and label to recent users
    const saved = localStorage.getItem('recentlyViewedUsers');
    const recentUsers = saved ? JSON.parse(saved) : [];
    const userId = user.id_member;
    const label = `${user.fullName} - ${user.email}`;
    const userEntry = { id: userId, label, date: new Date().toISOString() };
    const updatedUsers = [userEntry, ...recentUsers.filter((u) => u.id !== userId)].slice(0, 30);
    localStorage.setItem('recentlyViewedUsers', JSON.stringify(updatedUsers));
  }, [user.id_member, tenant.value, regions, user.email, user.fullName]);

  useEffect(() => {
    if (tenant.value !== LE_BUSINESS_TRAVELLER) {
      return;
    }
    if (employee?.roles) {
      setHasBusinessAdminSpoofRoles(hasBusinessAdminOrManagerRole(employee.roles));
    } else if (employee !== undefined) {
      // employee loaded but has no roles — default to false
      setHasBusinessAdminSpoofRoles(false);
    }
  }, [employee, tenant.value]);

  const getLuxPlusColour = () => {
    if (luxPlusFreePreview) {
      return teal[500];
    } else if (canRedeemLuxPlusBenefits) {
      return blue[900];
    }
  };

  const getLuxPlusLabel = () => {
    return `${checkCanRedeemLuxPlusBenefits(subscriptions) || luxPlusFreePreview ? '' : `${subscriptions?.status} `}${
      luxPlusFreePreview ? 'Free Preview ' : ''
    }${LUX_PLUS.PROGRAM_NAME} Profile`;
  };

  const regionsByCode = getRegionsByCountryCode(regions);
  const userCountry = regionsByCode[user.country_code];
  const fullTitle = getFullTitle();
  const subtitle = getSubtitle();

  const tabs: Record<TabKey, { label: string; showTab: boolean }> = {
    'user-details': {
      label: 'User Details',
      showTab: true,
    },
    'loyalty-programs-memberships': {
      label: 'Loyalty Programs Memberships',
      showTab: tenant.brand === LE_BUSINESS_TRAVELLER,
    },
    quotes: {
      label: 'Quotes',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'custom-offers': {
      label: 'Custom offers & quotes',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR]),
    },
    'luxplus-member-profile': {
      label: getLuxPlusLabel(),
      showTab:
        tenant.brand !== LE_BUSINESS_TRAVELLER &&
        hasLuxPlusSubscriptionStatus(subscriptions) &&
        canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'luxloyalty-member-profile': {
      label: `${LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} Profile`,
      showTab: !!luxLoyaltyAccount && canShowTabExceptRoles(user, [ROLE_EXPERIENCES_COORDINATOR]),
    },
    'user-agency': {
      label: 'Agency',
      showTab:
        isAgentHubSubTenant(tenant) && canShowTabExceptRoles(user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'recent-searches': {
      label: 'Recent Searches',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM, ROLE_ICS_STORE_TEAM]),
    },
    'recent-offer-page-views': {
      label: 'Recent Offer Page Views',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    'abandoned-carts': {
      label: 'Abandoned Carts',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    'similar-accounts': {
      label: 'Similar Accounts',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    'atol-certificates': {
      label: 'ATOL Certificates',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
  };

  const isTenantOfCurrentSite = validUserTenantForSiteTenant(tenant.value, user.tenant);
  const showSpoofActions = showSpoofUserBlock(tenant, user, tenancy);

  const NON_LEBT_TENANTS = new Set([LUXURY_ESCAPES, LE_AGENT_HUB, LE_AGENT_HUB_CVFR, SCOOPON, CUDO, DEALS, TREAT_ME]);

  // Button visibility configuration per tenant - positive structure
  const BUTTONS_MAP = {
    newPromo: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    promoRequests: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    referrals: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    newAtolEmail: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    partnershipButtons: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    verifyCustomerSMS: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    createOfferLink: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    newGiftCard: {
      tenants: new Set([LUXURY_ESCAPES, LE_BUSINESS_TRAVELLER]),
    },
  } as const;

  const shouldShowButton = (buttonKey: keyof typeof BUTTONS_MAP) => {
    const buttonConfig = BUTTONS_MAP[buttonKey];
    return buttonConfig.tenants.has(tenant.brand);
  };

  const showPromptToUseCustomOfferFlights = () => {
    if (['luxuryescapes', 'cudotravel', 'dealstravel', 'treatmetravel'].includes(tenant.brand)) {
      setShowFlightsWarningModal(true);
    } else {
      history.push(newFlightPageUri());
    }
  };

  return (
    <>
      <Dialog open={showFlightsWarningModal} onClose={() => setShowFlightsWarningModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>We are now using custom offer flights</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            ONLY use offline flights if custom offer flights is not working. Please escalate this to your manager.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you would like to proceed:
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              fullWidth
              component={MUILink}
              href={`/users/${user.id_member}/new-flight-order/traveller`}
              sx={{
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
              }}
            >
              Create Offline Flight
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => history.push(newCustomOfferUri(user, 'flight'))}
            >
              Create Custom Offer Flight
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowFlightsWarningModal(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Helmet>
        <title>Users | {user?.fullName || user.email}</title>
      </Helmet>

      {/* Custom user page header - all key info on one row */}
      <Box sx={{ pt: 1, pb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={1}>
          {/* Left side: name + status indicators */}
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography variant="h1">{fullTitle}</Typography>
            {subtitle}
          </Stack>

          {/* Right side: preferred sales agent */}
          {tenant.brand !== LE_BUSINESS_TRAVELLER && (
            <Stack direction="row" alignItems="center" spacing={1} flexShrink={0} sx={{ minWidth: 300 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Preferred Agent:
              </Typography>
              <EmployeeUserList
                user={user}
                isAdmin={isAdmin}
                onUpdatePreferredAgent={updatePreferredAgent}
                brand={tenant.brand}
              />
            </Stack>
          )}
        </Stack>
        <Divider />
      </Box>

      <FraudWarning userId={user?.id_member} />

      <ResolveAccountTakeoverInstructions user={user} />

      <Stack direction="column" gap={2}>
        {isTenantOfCurrentSite && (
          <>
            {!userCountry && (
              <Alert severity="warning">
                <strong>Error!</strong> There is no country set for this user. To view credits you need to add country.
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent={{ xs: 'start', md: 'space-between' }}
              gap={2}
            >
              <Stack direction="row" alignItems="start" flexWrap="wrap" gap={2}>
                <Button component={Link} variant="contained" to={purchasePageUri()}>
                  Purchases
                </Button>

                <PermissionedComponent
                  requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_TOUR_COORDINATOR]}
                >
                  <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_TOUR_COORDINATOR]}>
                    <Button
                      component={Link}
                      variant="contained"
                      to={creditsPageUri(userCountry)}
                      disabled={!userCountry}
                    >
                      Credits
                    </Button>
                  </PermissionedComponent>
                  <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                    <Button variant="contained" component={Link} className="T-new-order-btn" to={newOrderPageUri()}>
                      New Order
                    </Button>
                  </PermissionedComponent>

                  <PermissionedComponent
                    requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]}
                  >
                    {shouldShowButton('newPromo') && (
                      <Button variant="contained" onClick={showCreatePromoCodeModal}>
                        New Promo
                      </Button>
                    )}

                    {window.configs.OFFLINE_FLIGHT_PURCHASE_ENABLED === 'on' && (
                      <Button
                        variant="contained"
                        className="T-new-flight-btn"
                        onClick={showPromptToUseCustomOfferFlights}
                      >
                        New Flight
                      </Button>
                    )}

                    {shouldShowButton('newGiftCard') && (
                      <Button variant="contained" component={Link} to={newGiftCardOrderPageUri()}>
                        New Gift Card
                      </Button>
                    )}

                    {shouldShowButton('promoRequests') && (
                      <Button variant="contained" component={Link} to={promoRequestPageUri()}>
                        Promo Requests
                      </Button>
                    )}

                    {shouldShowButton('referrals') && (
                      <Button variant="contained" component={Link} to={referralLogPageUri()}>
                        Referrals
                      </Button>
                    )}
                    {shouldShowButton('newAtolEmail') && (
                      <Button variant="contained" component={Link} to={newAtolPageUri()}>
                        New Atol Email
                      </Button>
                    )}

                    <Button variant="contained" component={Link} to={newSentEmailUri()}>
                      Sent Emails
                    </Button>
                  </PermissionedComponent>
                </PermissionedComponent>

                <UpgradeToLuxPlusButton
                  customer={user}
                  refreshData={refreshData}
                  canViewLuxPlusBenefits={canViewLuxPlusBenefits}
                ></UpgradeToLuxPlusButton>
              </Stack>

              <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                <Stack direction="row" alignItems="start" flexWrap="wrap" flexShrink="0" gap={2}>
                  <Button variant="contained" color="error" onClick={showForceLogoutModal}>
                    Force logout
                  </Button>

                  <PermissionedComponent>
                    <Button variant="contained" color="error" onClick={gdprRemoval}>
                      DELETE ACCOUNT
                    </Button>
                  </PermissionedComponent>

                  <Button variant="contained" color="error" onClick={gdprRequest}>
                    REQUEST ACCOUNT DELETION
                  </Button>
                </Stack>
              </PermissionedComponent>
            </Stack>

            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
              <Stack direction="row" alignItems="start" flexWrap="wrap" gap={2}>
                <Button
                  variant="contained"
                  component={Link}
                  to={newCustomOfferUri(user)}
                  data-testid="new-custom-offer-btn"
                >
                  New custom offer
                </Button>
                <Button variant="contained" component={Link} to={historyUri(user)}>
                  History
                </Button>
                <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                  {shouldShowButton('partnershipButtons') && <PartnershipButtons user={user} />}
                  {shouldShowButton('verifyCustomerSMS') && <VerifyCustomerButtons user={user} />}
                </PermissionedComponent>
                <Button
                  variant="contained"
                  onClick={showEmailStatusDialog}
                  sx={{
                    bgcolor: purple[500],
                    color: 'white',
                    '&:hover': {
                      bgcolor: purple[700],
                    },
                  }}
                >
                  Email delivery status
                </Button>
              </Stack>
            </PermissionedComponent>
          </>
        )}

        <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
          <Stack direction="row" alignItems="start" flexWrap="wrap" gap={2}>
            {showSpoofActions && <SpoofUserBlock user={user} tenant={tenant} />}
            {tenant.value === LE_BUSINESS_TRAVELLER && showSpoofActions && hasBusinessAdminSpoofRoles && (
              <BusinessAdminSpoofButton user={user} />
            )}
            {shouldShowButton('createOfferLink') && (
              <Tooltip
                arrow
                title="Create a link for a customer to purchase an offer that is currently offline / expired. This saves us having to edit the offer schedule and re-open it so just one customer to purchase it."
              >
                <Button
                  variant="contained"
                  color="success"
                  component={Link}
                  to={`/users/${user.id_member}/offer-deep-link`}
                >
                  Create Offer Link
                </Button>
              </Tooltip>
            )}
            <TenancyWarningMessage user={user} />
          </Stack>
        </PermissionedComponent>

        <SamePhoneAccounts user={user} />

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={(_, value) => setCurrentTabHandler(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              position: 'relative',
              '& .MuiTabs-scrollButtons': {
                position: 'absolute',
                top: 0,
                bottom: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
                opacity: 1,
                '&.Mui-disabled': { opacity: 0, pointerEvents: 'none' },
                '&:first-of-type': { left: 0 },
                '&:last-of-type': { right: 0 },
              },
            }}
          >
            {Object.entries(tabs).map(
              ([tab, options]) => options.showTab && <Tab key={tab} value={tab} label={options.label} />,
            )}
          </Tabs>
        </Box>

        <Box>
          {currentTab === 'custom-offers' && <CustomOffersTab user={user} />}
          {currentTab === 'quotes' && <QuotesTab user={user} />}
          {currentTab === 'loyalty-programs-memberships' && <FrequentFlyerMembershipsTab user={user} />}
          {currentTab === 'user-details' && (
            <>
              <UpdateUser
                schema={schema.put}
                brand={tenant.brand}
                update={update}
                user={user}
                initialBusinessDetails={initialBusinessDetails}
                onBusinessDetailsSaved={handleBusinessDetailsSaved}
                ref={UpdateUserFormRef}
                linkedAccountEmail={
                  tenant.brand === LE_BUSINESS_TRAVELLER ? employee?.creditCustomerEmail ?? '' : undefined
                }
                linkedAccountId={tenant.brand === LE_BUSINESS_TRAVELLER ? employee?.creditCustomerId : undefined}
              />

              <dl>
                <dt className="h6 text-muted text-uppercase">Account created</dt>
                <dd>{formatDateOrdinalWithClock(user.created_date)}</dd>
              </dl>
              <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                <UserLoginTable userId={user.memberId} />
              </PermissionedComponent>

              <Typography variant="body1" color="textSecondary">
                Email changes history
              </Typography>
              <UserEmailChangeTable userId={user.memberId} />

              <Typography variant="body1" color="textSecondary">
                Phone number changes history:{' '}
                <MUILink component={Link} to={`/users/${user.id_member}/history?filter=user_changes`}>
                  See here for timeline of changes
                </MUILink>
              </Typography>

              {user.signup_domain && (
                <dl>
                  <dt className="h6 text-muted text-uppercase">Signup Domain</dt>
                  <dd>{user.signup_domain}</dd>
                </dl>
              )}

              {tenancy && tenancy.length > 0 && (
                <dl>
                  <dt className="h6 text-muted text-uppercase">Other domains that have user with same email</dt>
                  {tenancy.map((el) => (
                    <dd key={el.tenant}>{el.tenant}</dd>
                  ))}
                </dl>
              )}

              <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                <Box className="button-container">
                  <Button variant="contained" onClick={updateUser}>
                    Update
                  </Button>
                </Box>
              </PermissionedComponent>
            </>
          )}

          {currentTab === 'luxplus-member-profile' && (
            <MembershipSubscriptionDetails
              subscriptions={subscriptions}
              customerId={user.id_member}
              currentSubscriptionOrderItem={currentSubscriptionOrderItem}
              currentSubscriptionOrder={currentSubscriptionOrder}
              refreshData={refreshData}
            />
          )}

          {currentTab === 'luxloyalty-member-profile' && (
            <LuxLoyaltyUserProfile account={luxLoyaltyAccount} refreshData={refreshData} />
          )}

          {currentTab === 'user-agency' && <UserAgencyDetails id={user.id_member}></UserAgencyDetails>}
          {currentTab === 'recent-searches' && (
            <UserRecentSearchTable memberId={user.memberId} memberEmail={user.email} tenant={tenant} />
          )}
          {currentTab === 'recent-offer-page-views' && <UserRecentOfferPageViewsTable userId={user.memberId} />}
          {currentTab === 'abandoned-carts' && (
            <UserAbandonedCartsTable
              userId={user.memberId}
              email={user.email}
              region={user.country_code}
              tenant={tenant}
            />
          )}
          {currentTab === 'similar-accounts' && <SimilarAccountsTab user={user} />}
          {currentTab === 'atol-certificates' && <AtolCertificatesTab customerId={user.id_member} />}
        </Box>
        <CreatePromoForUserModal
          isModalOpen={isCreatePromoCodeModalVisible}
          closeModal={hideCreatePromoCodeModal}
          user={user}
        />

        <GDPRRemovalModal
          isGDPRRemovalModalVisible={isGDPRRemovalModalVisible}
          hideGDPRRemovalModal={hideGDPRRemovalModal}
          memberId={user.id_member}
          email={user.email}
          currencyCode={userCountry?.currencyCode}
          brand={tenant.brand}
        />
        <GDPRRequestModal
          isGDPRRequestModalVisible={isGDPRRequestModalVisible}
          hideGDPRRequestModal={hideGDPRRequestModal}
          memberId={user.id_member}
          email={user.email}
          currencyCode={userCountry?.currencyCode}
          brand={tenant.brand}
        />
        <ForceLogoutModal isOpen={isForceLogoutModalVisible} user={user} onClose={hideForceLogoutModal} />
        <EmailStatusCard
          open={isEmailStatusDialogOpen}
          onClose={hideEmailStatusDialog}
          userId={user.id_member}
          brand={tenant.brand}
        />
      </Stack>
    </>
  );
}

export const UserDetailsComponent = UserDetails;

export default withTenant(UserDetails);
